import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ActivityRendererProps } from './types';
import { 
  thinkingExerciseService, 
  Article, 
  Evaluation 
} from '../../../services/thinkingExerciseService';
import './CriticalThinkingActivity.css';

interface CriticalThinkingContent {
  exercise_id: string;
  title: string;
  description: string;
  article: Article;
  prompts: string[];
  evaluation_criteria?: string[];
  allow_custom_article?: boolean;
}

const CriticalThinkingActivity: React.FC<ActivityRendererProps> = ({ activity, onComplete }) => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const [content, setContent] = useState<CriticalThinkingContent | null>(null);
  const [userResponse, setUserResponse] = useState('');
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [useCustomArticle, setUseCustomArticle] = useState(false);
  const [customArticle, setCustomArticle] = useState<Article>({
    title: '',
    content: '',
    source: 'Custom',
    date: new Date().toISOString().split('T')[0]
  });
  const [previousResponse, setPreviousResponse] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'article' | 'analysis'>('article');
  const [promptsExpanded, setPromptsExpanded] = useState(true);

  useEffect(() => {
    if (activity.content) {
      setContent(activity.content as CriticalThinkingContent);
      
      // Load previous response if exists
      loadPreviousResponse(activity.content.exercise_id);
    }
  }, [activity]);

  const loadPreviousResponse = async (exerciseId: string) => {
    try {
      const response = await thinkingExerciseService.getResponse(exerciseId);
      if (response) {
        setPreviousResponse(response);
      }
    } catch (error) {
      console.error('Error loading previous response:', error);
    }
  };

  const handleSubmit = async () => {
    if (!content || !moduleId) return;

    // Validate response
    const validation = thinkingExerciseService.validateResponse(userResponse);
    if (!validation.isValid) {
      setErrorMessage(validation.error || 'Invalid response');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const articleToUse = useCustomArticle ? customArticle : content.article;

      const result = await thinkingExerciseService.submitResponse({
        moduleId: moduleId,
        activityId: activity.id,
        exerciseId: content.exercise_id,
        userResponse: userResponse,
        article: articleToUse,
        prompts: content.prompts,
        exerciseType: 'critical_thinking'
      });

      setEvaluation(result.evaluation);
      setShowFeedback(true);

      // Call onComplete with score
      onComplete(result.evaluation.score);
    } catch (error) {
      console.error('Error submitting response:', error);
      setErrorMessage('Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setUserResponse('');
    setEvaluation(null);
    setShowFeedback(false);
    setErrorMessage('');
  };

  const handleViewPrevious = () => {
    if (previousResponse) {
      setUserResponse(previousResponse.user_response);
      setEvaluation(previousResponse.ai_feedback);
      setShowFeedback(true);
    }
  };

  if (!content) {
    return (
      <div className="critical-thinking-activity">
        <div className="loading">Loading exercise...</div>
      </div>
    );
  }

  const articleToDisplay = useCustomArticle ? customArticle : content.article;
  const readingTime = thinkingExerciseService.estimateReadingTime(articleToDisplay.content);

  return (
    <div className="critical-thinking-activity">
      <div className="activity-header-ct">
        <h2 className="activity-title-ct">{content.title}</h2>
        <p className="activity-description-ct">{content.description}</p>
      </div>

      {/* Tab Navigation */}
      {!showFeedback && (
        <div className="tab-navigation-ct">
          <button
            className={`tab-button-ct ${activeTab === 'article' ? 'active' : ''}`}
            onClick={() => setActiveTab('article')}
          >
            Article & Prompts
          </button>
          <button
            className={`tab-button-ct ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            Your Analysis
            {userResponse.length > 0 && (
              <span className="tab-badge-ct">{userResponse.length}</span>
            )}
          </button>
        </div>
      )}

      {/* Article Tab */}
      {!showFeedback && activeTab === 'article' && (
        <>
          <div className="article-section-ct">
            <div className="article-meta-ct">
              <div className="article-meta-left-ct">
                {!useCustomArticle && (
                  <>
                    <span className="article-source-ct">{articleToDisplay.source}</span>
                    {articleToDisplay.date && (
                      <span className="article-date-ct">{articleToDisplay.date}</span>
                    )}
                  </>
                )}
                <span className="reading-time-ct">{readingTime} min read</span>
              </div>
              {content.allow_custom_article && (
                <button
                  className="toggle-custom-article-ct"
                  onClick={() => setUseCustomArticle(!useCustomArticle)}
                >
                  {useCustomArticle ? 'Use Provided Article' : 'Use Custom Article'}
                </button>
              )}
            </div>

            {useCustomArticle ? (
              <div className="custom-article-form-ct">
                <div className="form-group-ct">
                  <label className="form-label-ct">Article Title</label>
                  <input
                    type="text"
                    className="form-input-ct"
                    value={customArticle.title}
                    onChange={(e) => setCustomArticle({ ...customArticle, title: e.target.value })}
                    placeholder="Enter article title"
                  />
                </div>
                <div className="form-group-ct">
                  <label className="form-label-ct">Source</label>
                  <input
                    type="text"
                    className="form-input-ct"
                    value={customArticle.source}
                    onChange={(e) => setCustomArticle({ ...customArticle, source: e.target.value })}
                    placeholder="Enter source (e.g., The New York Times)"
                  />
                </div>
                <div className="form-group-ct">
                  <label className="form-label-ct">Article URL (optional)</label>
                  <input
                    type="url"
                    className="form-input-ct"
                    value={customArticle.url || ''}
                    onChange={(e) => setCustomArticle({ ...customArticle, url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group-ct">
                  <label className="form-label-ct">Article Content</label>
                  <textarea
                    className="form-textarea-ct article-content-input-ct"
                    value={customArticle.content}
                    onChange={(e) => setCustomArticle({ ...customArticle, content: e.target.value })}
                    placeholder="Paste the article content here..."
                    rows={12}
                  />
                </div>
              </div>
            ) : (
              <div className="article-display-ct">
                <h3 className="article-title-ct">{articleToDisplay.title}</h3>
                {articleToDisplay.url && (
                  <a
                    href={articleToDisplay.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="article-link-ct"
                  >
                    Read Original →
                  </a>
                )}
                <div className="article-content-ct">
                  {articleToDisplay.content.split('\n').map((paragraph, index) => (
                    paragraph.trim() && <p key={index} className="article-paragraph-ct">{paragraph}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="tab-action-ct">
            <button
              className="next-tab-btn-ct"
              onClick={() => setActiveTab('analysis')}
            >
              Continue to Analysis →
            </button>
          </div>
        </>
      )}

      {/* Analysis Tab */}
      {!showFeedback && activeTab === 'analysis' && (
        <div className="response-section-ct">
          {/* Analysis Prompts - Collapsible */}
          <div className="prompts-section-ct">
            <button 
              className="prompts-toggle-ct"
              onClick={() => setPromptsExpanded(!promptsExpanded)}
            >
              <span className="prompts-toggle-text-ct">Analysis Prompts</span>
              <span className="prompts-toggle-arrow-ct">{promptsExpanded ? '▼' : '▶'}</span>
            </button>
            {promptsExpanded && (
              <div className="prompts-content-ct">
                <p className="prompts-subtitle-ct">Consider these questions in your analysis:</p>
                <ul className="prompts-list-ct">
                  {content.prompts.map((prompt, index) => (
                    <li key={index} className="prompt-item-ct">{prompt}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="response-header-ct">
            <h3 className="response-title-ct">Your Analysis</h3>
            {previousResponse && (
              <button className="view-previous-btn-ct" onClick={handleViewPrevious}>
                View Previous Attempt
              </button>
            )}
          </div>
          <textarea
            className="response-textarea-ct"
            value={userResponse}
            onChange={(e) => setUserResponse(e.target.value)}
            placeholder="Write your critical analysis here. Be specific and reference the article. Minimum 50 characters."
            rows={12}
          />
          <div className="response-meta-ct">
            <span className="character-count-ct">
              {userResponse.length} characters
              {userResponse.length < 50 && ` (minimum 50 required)`}
            </span>
          </div>

          {errorMessage && (
            <div className="error-message-ct">{errorMessage}</div>
          )}

          <button
            className="submit-btn-ct"
            onClick={handleSubmit}
            disabled={isSubmitting || userResponse.length < 50}
          >
            {isSubmitting ? 'Analyzing...' : 'Submit for AI Feedback'}
          </button>
        </div>
      )}

      {/* AI Feedback */}
      {showFeedback && evaluation && (
        <div className="feedback-section-ct">
          <div className="feedback-header-ct">
            <h3 className="feedback-title-ct">AI Feedback</h3>
            <div 
              className="score-display-ct" 
              style={{ color: thinkingExerciseService.getScoreColor(evaluation.score) }}
            >
              <span className="score-number-ct">{evaluation.score}</span>
              <span className="score-label-ct">{thinkingExerciseService.getScoreLabel(evaluation.score)}</span>
            </div>
          </div>

          <div className="feedback-summary-ct">
            <p>{evaluation.summary}</p>
          </div>

          <div className="feedback-strengths-ct">
            <h4 className="feedback-subtitle-ct">✓ Strengths</h4>
            <ul className="feedback-list-ct">
              {evaluation.strengths.map((strength, index) => (
                <li key={index} className="feedback-item-ct strength-item-ct">{strength}</li>
              ))}
            </ul>
          </div>

          <div className="feedback-improvements-ct">
            <h4 className="feedback-subtitle-ct">→ Areas for Improvement</h4>
            <ul className="feedback-list-ct">
              {evaluation.improvements.map((improvement, index) => (
                <li key={index} className="feedback-item-ct improvement-item-ct">{improvement}</li>
              ))}
            </ul>
          </div>

          <div className="feedback-detailed-ct">
            <h4 className="feedback-subtitle-ct">Detailed Feedback</h4>
            <div className="feedback-detailed-content-ct">
              {evaluation.detailedFeedback.split(/\n\n+/).map((paragraph, index) => {
                const trimmed = paragraph.trim();
                // Handle potential single \n within paragraphs by replacing with space
                const cleaned = trimmed.replace(/\n/g, ' ');
                return cleaned ? <p key={index}>{cleaned}</p> : null;
              })}
            </div>
          </div>

          <div className="feedback-actions-ct">
            <button className="try-again-btn-ct" onClick={handleTryAgain}>
              Try Another Analysis
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CriticalThinkingActivity;

